
import { Vue, Component, Prop } from "vue-property-decorator";
import { Project, User, Company } from "@/generated/graphql";
import GET_PROJECT_CARE_PLAN from "@/apollo/projectActivities/GetProjectCarePlan.gql";
import GET_PROJECT_CLIENT from "@/apollo/projectActivities/GetProjectClient.gql";
import ClientInformation from "./CarePlanRightSide/ClientInformation.vue";
import RisksAndAlerts from "./CarePlanRightSide/RisksAndAlerts.vue";
import ClientServices from "./CarePlanRightSide/ClientServices.vue";
import Goals from "./CarePlanRightSide/Goals.vue";
import Interventions from "./CarePlanRightSide/Interventions.vue";
import FunctionalAssessment from "./CarePlanRightSide/FunctionalAssessment.vue";
import HomeEnvironment from "./CarePlanRightSide/HomeEnvironment.vue";
import PersonalConsiderations from "./CarePlanRightSide/PersonalConsiderations.vue";
import DocumentationRequirements from "./CarePlanRightSide/DocumentationRequirements.vue";
import ClientEditModal from "@/components/companyProjects/ClientEditModal.vue";
import _ from "lodash";

@Component({
  components: {
    ClientInformation,
    RisksAndAlerts,
    ClientServices,
    Goals,
    Interventions,
    FunctionalAssessment,
    HomeEnvironment,
    PersonalConsiderations,
    DocumentationRequirements,
    ClientEditModal,
  },
  apollo: {
    carePlanData: {
      query: GET_PROJECT_CARE_PLAN,
      variables() {
        return {
          project_id: this.$route.params.id,
        };
      },
      update(data) {
        if (data.client_care_plan && data.client_care_plan.length > 0) {
          this.isCareplanExist = true;
          return data.client_care_plan[0];
        }
        else{
          this.isCareplanExist = false;
        }
        return {};
      },
      error(error) {
        console.error("Error fetching care plan data:", error);
      },
    },
    clientData: {
      query: GET_PROJECT_CLIENT,
      variables() {
        return {
          where: {
            id: {
              _eq: this.$route.params.id,
            },
          },
        };
      },
      update({ project }) {
        if (project?.[0]) {
          this.projectClientData = project[0];
          return project[0];
        }
        return null;
      },
      error(error) {
        console.error("Error fetching client data:", error);
      },
    },
  },
})
export default class CarePlanNew extends Vue {
  @Prop({ type: Object, required: true }) readonly project!: Project;
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;

  carePlanData: any = {};
  isCareplanExist: boolean = true;
  projectClientData: any = {};
  showEditModal = false;
  formattedCarePlanData: any = {};
  showMobileDropdown = false;
  currentEditTab = { tab: 0, step: 0 };

  activeTab = "clientInformation";

  tabMapping: Record<string, { tab: number; step: number }> = {
    clientInformation: { tab: 2, step: 0 },
    risksAndAlerts: { tab: 2, step: 1 },
    clientServices: { tab: 2, step: 2 },
    goals: { tab: 2, step: 3 },
    interventions: { tab: 2, step: 4 },
    functionalAssessment: { tab: 2, step: 5 },
    homeEnvironment: { tab: 2, step: 6 },
    personalConsiderations: { tab: 2, step: 7 },
    documentationRequirements: { tab: 2, step: 8 },
  };

  // Define arrays and select fields for data formatting
  arrayFields = [
    "client_services",
    "pets",
    "accessibility_issues",
    "safety_concerns",
    "cultural_practices",
    "hobbies",
    "social_connections",
    "allergy_food",
    "allergy_medication",
    "allergy_environmental",
  ];

  selectFields = [
    "gender",
    "fall_risk",
    "dnr_order",
    "dnr_copy_attached",
    "client_services",
    "frequency",
    "dietary_goals_frequency",
    "housekeeping_assistance_level",
    "bathing_frequency",
    "bathing_supervision_level",
    "mobility_level",
    "toileting_frequency",
  ];

  selectMobileTab(tabId: string): void {
    this.activeTab = tabId;
    this.showMobileDropdown = false;
  }

  handleEdit(): void {
      const mergedData = { ...this.carePlanData, ...this.projectClientData };
    // const formattedData = this.formatDataForModal(mergedData);
    this.showEditModal = true;

    const mapping = this.tabMapping[this.activeTab];
    if (mapping) {
      this.currentEditTab = mapping;
    }

    this.formattedCarePlanData = mergedData;
  }

hanldeCancel(): void {
  this.showEditModal = false;
}

  closeEditModal(): void {
    this.showEditModal = false;
    // window.location.reload();
  }

  // Debounced form data update
  private debouncedUpdateForm = _.debounce((newData: any) => {
    this.carePlanData = { ...this.carePlanData, ...newData };
  }, 300);

  updateFormData(newData: any): void {
    this.debouncedUpdateForm(newData);
  }

  // Debounced draft saving
  private debouncedSaveDraft = _.debounce(async (draftData: any) => {
    try {
      const draftKey = `carePlanFormDraft_${this.$route.params.id}`;
      const cleanedData = _.omit(draftData, ["__typename"]);
      localStorage.setItem(draftKey, JSON.stringify(cleanedData));
    } catch (error) {
      console.error("Error saving draft:", error);
    }
  }, 500);

  async handleSaveDraft(draftData: any): Promise<void> {
    this.debouncedSaveDraft(draftData);
  }

  convertArrayToOptions(
    array: string[] | null | undefined
  ): Array<{ label: string; value: string }> {
    if (!Array.isArray(array)) return []; // Ensure it's an array
    return array.map((item) => ({
      label: item,
      value: item,
    }));
  }

  convertOptionsToArray(
    options: Array<{ label: string; value: string }>
  ): string[] {
    if (!options) return [];
    return options.map((option) => option.value);
  }

  formatDataForModal(data: any): any {
    const formattedData = _.cloneDeep(data);

    this.arrayFields.forEach((field) => {
      if (formattedData[field]) {
        formattedData[field] = this.convertArrayToOptions(formattedData[field]);
      }
    });

    // Convert single select fields to option format
    this.selectFields.forEach((field) => {
      if (formattedData[field] && !this.arrayFields.includes(field)) {
        formattedData[field] = {
          label: formattedData[field],
          value: formattedData[field],
        };
      }
    });

    return formattedData;
  }

  // Sidebar tabs based on the Figma design
  sidebarTabs = [
    { id: "clientInformation", name: "Client Information" },
    { id: "risksAndAlerts", name: "Risks and Alerts" },
    { id: "clientServices", name: "Client Services" },
    { id: "goals", name: "Goals" },
    { id: "interventions", name: "Interventions" },
    { id: "functionalAssessment", name: "Functional Assessment" },
    { id: "homeEnvironment", name: "Home Environment" },
    { id: "personalConsiderations", name: "Personal Considerations" },
    { id: "documentationRequirements", name: "Documentation Requirements" },
  ];

  get getActiveTabName(): string {
    return (
      this.sidebarTabs.find((tab) => tab.id === this.activeTab)?.name || ""
    );
  }
}
